import React, { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { MapPin, Truck, Check, X, Loader2, Clock, Package } from 'lucide-react';
import axios from 'axios';

export default function ShippingCalculator({ productId, onShippingCalculated }) {
  const [pincode, setPincode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const calculateShipping = async () => {
    if (pincode.length !== 6) {
      setError('Please enter a valid 6-digit pincode');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/shipping/calculate`, {
        product_id: productId,
        delivery_pincode: pincode,
        quantity: 1
      });

      setResult(response.data);
      
      if (onShippingCalculated) {
        onShippingCalculated(response.data);
      }
    } catch (err) {
      console.error('Shipping calculation error:', err);
      setError(err.response?.data?.detail || 'Unable to check delivery');
    } finally {
      setLoading(false);
    }
  };

  const handlePincodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPincode(value);
    setResult(null);
    setError('');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <MapPin className="w-4 h-4" />
        Check Delivery
      </div>
      
      <div className="flex gap-2">
        <Input
          type="text"
          value={pincode}
          onChange={handlePincodeChange}
          placeholder="Enter pincode"
          className="flex-1"
          maxLength={6}
          data-testid="shipping-pincode-input"
        />
        <Button
          onClick={calculateShipping}
          disabled={loading || pincode.length !== 6}
          variant="outline"
          className="shrink-0"
          data-testid="check-shipping-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm p-2 bg-red-50 rounded-lg">
          <X className="w-4 h-4" />
          {error}
        </div>
      )}

      {result && (
        <div className={`p-3 rounded-lg border ${result.serviceable ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          {result.serviceable ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-700 font-medium">
                <Check className="w-4 h-4" />
                Delivery Available
              </div>
              
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Truck className="w-4 h-4 text-green-600" />
                  <span>₹{result.shipping_cost?.toFixed(0) || 'Free'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span>{result.estimated_days || 5} days</span>
                </div>
              </div>

              {result.cod_available && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                  <Package className="w-4 h-4 text-amber-600" />
                  <span>Cash on Delivery available</span>
                </div>
              )}

              {result.all_options && result.all_options.length > 1 && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <p className="text-xs text-gray-500 mb-2">Other shipping options:</p>
                  <div className="space-y-1">
                    {result.all_options.slice(1, 3).map((opt, idx) => (
                      <div key={idx} className="flex justify-between text-xs text-gray-500">
                        <span>{opt.courier_name}</span>
                        <span>₹{opt.freight_charge?.toFixed(0)} • {opt.estimated_days} days</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-red-700">
              <X className="w-4 h-4" />
              <span className="text-sm">{result.message || 'Delivery not available'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
